/**
 * Peer bullet charts: expand the horizontal domain so markers are never clamped
 * outside the visible peer display band (p10–p90).
 */
export function computeAbsoluteDomain(
  displayMin: number,
  displayMax: number,
  actual: number,
  avg: number,
  median: number
): readonly [number, number] {
  const min = Math.min(displayMin, actual, avg, median);
  const max = Math.max(displayMax, actual, avg, median);
  return [min, max] as const;
}

/**
 * School profile peer comparison: axis spans true peer min/max (plus padding) so
 * lowest/highest schools sit at real positions; include actual/avg/median if outside range.
 */
export function computePeerFullDomain(
  rawMin: number,
  rawMax: number,
  actual: number,
  avg: number,
  median: number,
  padFraction = 0.02,
  options?: { floorAtZero?: boolean }
): readonly [number, number] {
  const floorAtZero = options?.floorAtZero !== false;
  const lo0 = Math.min(rawMin, actual, avg, median);
  const hi0 = Math.max(rawMax, actual, avg, median);
  const snapLoToPeerMin = (lo: number): number => {
    let x = lo;
    /** Do not pad below the cohort’s true min — avoids showing $0 when peer min is e.g. $158. */
    if (Number.isFinite(rawMin)) x = Math.max(x, rawMin);
    if (floorAtZero) x = Math.max(0, x);
    return x;
  };

  if (!(hi0 > lo0)) {
    const pad = Math.max(Math.abs(lo0) * 0.05, 1);
    let lo = snapLoToPeerMin(lo0 - pad);
    let hi = lo0 + pad;
    if (!(hi > lo)) hi = lo + Math.max(Math.abs(lo) * 0.02, 1);
    return [lo, hi] as const;
  }
  const span = hi0 - lo0;
  const pad = span * padFraction;
  let lo = snapLoToPeerMin(lo0 - pad);
  let hi = hi0 + pad;
  if (!(hi > lo)) hi = lo + Math.max(span * 0.02, 1);
  return [lo, hi] as const;
}
